import { HttpClient } from "@angular/common/http";
import { Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import * as CryptoJS from 'crypto-js';
import { NGXLogger } from "ngx-logger";
import { Table } from "primeng/table";
import { Asset } from "src/app/models/assets.model";
import { ChapterSection } from "src/app/models/chapter-sections.model";
import { AssetsService } from "src/app/services/assets.service";
import { ChapterSectionsService } from "src/app/services/chapter-sections.service";
import { ChaptersService } from "src/app/services/chapters.service";
import { QuizesService } from "src/app/services/quizes.service";
import * as environment from "src/environments/environment";

@Component({
  selector: "app-chapter-sections",
  templateUrl: "./chapter-sections.component.html",
  styleUrls: ["./chapter-sections.component.css"],
})
export class ChapterSectionsComponent implements OnInit {
  @ViewChild("dt") private dt: Table;

  first = 0;
  rows = 15;
  sortField;
  sortOrder;
  filters;
  totalRecords = 0;

  displayDialog: boolean;

  selectedSection: ChapterSection;

  newSectionHeader: boolean;
  newSubsection: boolean;

  sections: ChapterSection[] = [];
  sectionHeaders: ChapterSection[] = [];
  /** Lightweight list of every top-level header for the chapter (for dropdowns/reorder). */
  allHeaders: ChapterSection[] = [];
  /** Lightweight list of every section (headers + subsections) in the chapter, so the
   * "After" dropdown and reorder math for a header's subsections aren't limited to
   * whichever subsections landed on the current flat page. */
  allSections: { id: number; name: string; order: number; parentId: number }[] = [];
  /** Last PrimeNG lazy-load event, so we can re-run the current page after edits. */
  lazyEvent: any = null;
  subSections: { number: ChapterSection[] } = {
    number: []
  };
  section: ChapterSection;
  targets = [{ label: "Same page", value: "_self" }, { label: "New page", value: "_blank" }];

  cols: any[];

  errorMessage;
  successMessage;

  dateFilters: any;

  currentCourseId: number;
  currentChapterId: number;
  currentChapterName: string;

  courseChapters = [];
  chapterSections = [];
  links = [];

  sectionForm = new FormGroup({
    id: new FormControl("", Validators.required),
    chapterId: new FormControl("", Validators.required),
    name: new FormControl("", Validators.required),
    description: new FormControl(""),
    content: new FormControl(""),
    location: new FormControl("", Validators.required),
    target: new FormControl("", Validators.required),
    order: new FormControl("", Validators.required),
  });

  loading = true;
  sectionOrders: any[];

  editorConfig: any;
  tinyMceInit = {
    plugins: 'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen media table code wordcount',
    toolbar:
      'undo redo | formatselect | bold italic backcolor | \
      alignleft aligncenter alignright alignjustify | \
      bullist numlist outdent indent | removeformat | link image',
    target_list: [
      { title: 'Same page', value: '_self' },
      { title: 'New page', value: '_blank' }
    ],
    default_link_target: '_blank',
    height: '700', width: '100%',
    paste_data_images: false,
    images_upload_handler: async (blobInfo) => {
      return await this.uploadToS3(blobInfo);
    },
  };

  constructor(
    private logger: NGXLogger,
    private sectionsService: ChapterSectionsService,
    private chaptersService: ChaptersService,
    private quizesService: QuizesService,
    private assetsService: AssetsService,
    public router: Router,
    private activateRoute: ActivatedRoute,
    private http: HttpClient
  ) { }

  async uploadToS3(blobInfo) {
    if (blobInfo?.blobUri()?.includes('assets/courses')) {
      return
    }

    const contentType = blobInfo.blob().type;
    const size = blobInfo.blob().size;

    const hash = CryptoJS.SHA256(contentType + size.toString()).toString(CryptoJS.enc.Hex);

    const assetName = `${hash}`;
    const assetRequest = {
      assetName: assetName,
      contentType: blobInfo.blob().type,
      assetType: 'image',
      course: { id: this.currentCourseId },
      chapterSection: this.section,
    } as Asset;

    const asset = await this.assetsService.getSignedUrl(assetRequest);
    await this.assetsService.putSignedUrl(asset.signedUrl, blobInfo.blob());
    return `${environment.environment.s3BucketUrl}${asset.location}`;
  }

  updateConfig() {
    this.editorConfig = {
      plugins: 'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen media table code wordcount',
      toolbar:
        'undo redo | formatselect | bold italic backcolor | \
      alignleft aligncenter alignright alignjustify | \
      bullist numlist outdent indent | removeformat | link image',
      target_list: [
        { title: 'Same page', value: '_self' },
        { title: 'New page', value: '_blank' }
      ],
      default_link_target: '_blank',
      height: '700', width: '100%',
      paste_data_images: false,
      images_upload_handler: async (blobInfo) => {
        return await this.uploadToS3(blobInfo);
      },
    };
  }

  /**
   * PrimeNG lazy loader: fetches only the current page of headers (with their
   * subsections) from the server, plus the lightweight full header list used by
   * the ordering/parent dropdowns. Paginator pages by header count.
   */
  async loadSectionsLazy(event: any) {
    if (!this.currentCourseId || !this.currentChapterId) {
      // Route params not resolved yet; remember the event and run it via reload().
      this.lazyEvent = event;
      return;
    }
    this.lazyEvent = event;
    this.loading = true;
    this.first = event && event.first != null ? event.first : 0;
    this.rows = event && event.rows != null ? event.rows : this.rows;

    const res = await this.sectionsService.findAllPaged(this.currentCourseId, this.currentChapterId, {
      first: this.first,
      rows: this.rows,
      sortField: event && event.sortField,
      sortOrder: event && event.sortOrder,
      filters: event && event.filters,
    });

    // Support both the paginated shape { data, totalRecords, headers } and a
    // plain array response (e.g. if the paginated backend isn't deployed yet).
    const raw: any = res;
    const data: any[] = Array.isArray(raw) ? raw : (raw.data || []);
    // A row is a header when it has no parent. parentId can arrive as a number,
    // null, or a string ("0" for bigint columns), so compare numerically.
    const isHeader = (s: any) => !Number(s.parentId);

    const paged = !Array.isArray(raw) && Array.isArray(raw.data);

    const headerList = !Array.isArray(raw) && raw.headers && raw.headers.length
      ? raw.headers
      : data.filter(isHeader);
    this.allHeaders = headerList.slice().sort((a, b) => a.order - b.order);
    // Fall back to the current page's rows if the backend doesn't send allSections yet.
    this.allSections = !Array.isArray(raw) && raw.allSections && raw.allSections.length
      ? raw.allSections
      : data;
    // Flat pagination: totalRecords is the count of ALL sections in the chapter.
    this.totalRecords = paged && raw.totalRecords != null ? raw.totalRecords : data.length;

    // Group the current page's rows (used by the edit dialog / reorder helpers).
    this.sectionHeaders = data.filter(isHeader).sort((a, b) => a.order - b.order);
    this.subSections = { number: [] } as any;
    this.sectionHeaders.forEach((s) => (this.subSections[s.id] = []));
    data.filter((s) => !isHeader(s)).forEach((s) => {
      if (!this.subSections[s.parentId]) {
        this.subSections[s.parentId] = [];
      }
      this.subSections[s.parentId].push(s);
    });
    Object.keys(this.subSections).forEach((k) => {
      if (Array.isArray(this.subSections[k])) {
        this.subSections[k] = this.subSections[k].sort((a, b) => a.order - b.order);
      }
    });

    // The server returns the page already ordered header-then-subsections; render
    // it as-is. (Array fallback: build the flat order client-side.)
    this.sections = paged
      ? data
      : this.sectionHeaders.flatMap((s) => [s, ...(this.subSections[s.id] || [])]);
    // Parent dropdown lists all headers (a subsection nests under a header).
    this.chapterSections = this.allHeaders.map((h) => ({ label: h.name, value: h.id }));

    this.loading = false;
  }

  /** Re-run the current page (after create/update/delete/reorder). */
  async reload() {
    await this.loadSectionsLazy(this.lazyEvent || { first: this.first, rows: this.rows });
  }

  /**
   * True when a row is a top-level header. parentId can be a number, null, or a
   * string ("0" for bigint columns), so compare numerically — otherwise headers
   * with parentId "0" render as subsections (grey header rows disappear).
   */
  isHeaderRow(row: any): boolean {
    return !Number(row && row.parentId);
  }

  /**
   * Persist ordering for a section within its group (headers or a parent's
   * subsections) based on the chosen "After" target, via the lightweight reorder
   * endpoint so content is never overwritten.
   */
  private async applyReorder(sectionId: number, parentId: number, afterId: number) {
    let group: { id: number; order: number }[];
    if (parentId) {
      // Use the full chapter-wide subsection list (not just the current page's
      // subSections[parentId]) so renumbering doesn't strand siblings that
      // didn't land on this page with stale order values.
      group = this.allSections
        .filter((s) => Number(s.parentId) === Number(parentId))
        .sort((a, b) => a.order - b.order);
    } else {
      group = [...this.allHeaders].sort((a, b) => a.order - b.order);
    }

    const moving = group.find((s) => s.id == sectionId);
    if (!moving) {
      return; // not part of the loaded group; nothing to reorder
    }
    group = group.filter((s) => s.id != sectionId);
    if (afterId == null) {
      group.unshift(moving);
    } else {
      const idx = group.findIndex((s) => s.id == afterId);
      if (idx >= 0) {
        group.splice(idx + 1, 0, moving);
      } else {
        group.push(moving);
      }
    }

    const payload = group.map((s, idx) => ({ id: s.id, order: idx + 1, parentId: parentId || 0 }));
    await this.sectionsService.reorder(this.currentCourseId, this.currentChapterId, payload);
  }

  ngOnInit() {
    this.cols = [
      { field: "id", header: "ID" },
      { field: "name", header: "Name" },
      { field: "location", header: "Location" },
      { field: "target", header: "Target" },
    ];


    this.activateRoute.paramMap.subscribe(async (paramMap) => {
      const params = paramMap && paramMap["params"];
      if (params && params["courseId"] && params["chapterId"]) {
        this.currentCourseId = params["courseId"];
        this.currentChapterId = params["chapterId"];

        if (this.courseChapters.length === 0) {
          const chapters = await this.chaptersService.getChaptersBy(this.currentCourseId);
          this.courseChapters = chapters.map((c) => {
            return { label: c.ChapterName, value: c.ID };
          });
        }

        this.currentChapterName = this.courseChapters.find((c) => c.value == this.currentChapterId)?.label;

        if (this.links.length === 0) {
          const quizzes = await this.quizesService.findAllPerCourse(this.currentCourseId);
          this.links = quizzes.map((q) => {
            return { label: 'Quiz - ' + q.QuizTitle, value: `courses/${this.currentCourseId}/chapters/${this.currentChapterId}/quizzes/${q.QuizID}` };
          });
          const assets = await this.assetsService.findAll();
          this.links.push(...(assets.map((a) => {
            return { label: 'Asset - ' + a.assetName, value: `courses/${this.currentCourseId}/chapters/${this.currentChapterId}/assets/${a.id}` };
          })));
          // console.log(JSON.stringify(this.links));
        }

        await this.reload();
      } else {
        this.errorMessage = "No chapter sections found";
        this.loading = false;
        return;
      }
    });
    this.updateConfig();
  }

  addHeader() {
    this.clearAll();
    this.newSectionHeader = true;
    this.displayDialog = true;
    this.fillSectionHeaderOrders(0);
  }

  addSubsection(parentId: number) {
    this.clearAll();
    this.section.parentId = parentId;
    this.newSubsection = true;
    this.displayDialog = true;
    this.fillSubsectionOrders(0, parentId);
  }

  async save() {
    const afterId = this.section["afterId"];
    try {
      this.section.location = this.section.location?.trim();
      delete this.section["afterId"];

      // parentId can be a string ("0" for bigint columns); coerce numerically so
      // a header (parentId 0) isn't treated as a subsection, which made
      // applyReorder look in the wrong (empty) group and skip the reorder call.
      const parentId = Number(this.section.parentId) || 0;

      if (this.section.id) {
        // Persist the section's own fields, then reposition within its group.
        await this.sectionsService.update(this.currentCourseId, this.currentChapterId, this.section.id, this.section);
        await this.applyReorder(this.section.id, parentId, afterId);
      } else {
        // New section: append to the end of its group (order = max + 1). It can
        // be repositioned via "After" on a subsequent edit once it has an id.
        this.section.parentId = parentId;
        const group = parentId
          ? this.allSections.filter((s) => Number(s.parentId) === Number(parentId))
          : this.allHeaders;
        const maxOrder = group.length ? Math.max(...group.map((s) => s.order || 0)) : 0;
        this.section.order = maxOrder + 1;
        await this.sectionsService.create(this.currentCourseId, this.currentChapterId, this.section);
      }

      await this.reload();
      this.successMessage = "Section saved successfully";
    } catch (err) {
      if (
        err &&
        err.status === 400 &&
        err.error &&
        err.error.error &&
        err.error.error.description
      ) {
        this.errorMessage = err.error.error.description;
      }
    }
  }

  onRowSelect(event) {
    this.newSectionHeader = false;
    this.clearAll();
    this.section = Object.assign({} as ChapterSection, event.data);
    const subsection = !this.isHeaderRow(this.section);
    if (subsection) {
      const siblings = this.allSections
        .filter((s) => Number(s.parentId) === Number(this.section.parentId))
        .sort((a, b) => a.order - b.order);
      const idx = siblings.findIndex((s) => s.id == this.section.id);
      this.section["afterId"] = idx > 0 ? siblings[idx - 1]?.id : null;
      this.fillSubsectionOrders(this.section.id, this.section.parentId);
    } else {
      const idx = this.sectionHeaders.findIndex((s) => s.id == this.section.id);
      this.section["afterId"] = idx > 0 ? this.sectionHeaders[idx - 1]?.id : null;
      this.fillSectionHeaderOrders(this.section.id);
    }
    this.displayDialog = true;
  }

  fillSectionHeaderOrders(id: number) {
    // Use the full header list (not just the current page) so "After" can target
    // any header in the chapter.
    this.sectionOrders = this.allHeaders
      .filter((s) => s.id !== id)
      .sort((a, b) => a.order - b.order)
      .map((s) => {
        return { label: s.name, value: s.id };
      });
  }

  fillSubsectionOrders(id: number, parentId: number) {
    // Use the full chapter-wide section list (not just the current page) so
    // "After" can target any subsection under this parent, regardless of
    // which flat page it happens to have landed on.
    this.sectionOrders = this.allSections
      .filter((c) => Number(c.parentId) === Number(parentId))
      .filter((s) => s.id !== id)
      .sort((a, b) => a.order - b.order)
      .map((s) => {
        return { label: s.name, value: s.id };
      });
  }

  clearAll() {
    this.section = { chapterId: this.currentChapterId } as ChapterSection;
    this.errorMessage = null;
    this.successMessage = null;
    this.newSectionHeader = false;
    this.newSubsection = false;
  }

  async delete() {
    this.loading = true;
    const parentId = this.section.parentId || 0;
    await this.sectionsService.delete(this.currentCourseId, this.currentChapterId, this.section.id);

    // Renumber remaining siblings (order-only) so ordering stays gap-free. Use
    // the full chapter-wide list for subsections (not the current page) so
    // siblings on other pages aren't left with stale order values.
    const group = parentId
      ? this.allSections.filter((s) => Number(s.parentId) === Number(parentId) && s.id !== this.section.id)
      : this.allHeaders.filter((s) => s.id !== this.section.id);
    if (group.length) {
      const payload = group
        .sort((a, b) => a.order - b.order)
        .map((s, idx) => ({ id: s.id, order: idx + 1, parentId }));
      await this.sectionsService.reorder(this.currentCourseId, this.currentChapterId, payload);
    }

    await this.reload();
    this.successMessage = "Section deleted successfully";
  }

  handleClose() {
    this.clearAll();
    this.displayDialog = false;
    this.logger.info(`Closing dialog...`);
  }

  selectedParent() {
    console.log(`Parent changed to ${this.section.parentId} - resetting afterId`);
    this.fillSubsectionOrders(this.section.id, this.section.parentId);
    this.section["afterId"] = null;
  }


}
